/**
 * Zoom-based label visibility configuration.
 *
 * At lower zoom levels (more zoomed out), small countries are hidden
 * to reduce clutter. As zoom increases, more labels become visible.
 */

/**
 * Countries to hide at each zoom level.
 * At zoom N, hide all countries in HIDDEN_AT_ZOOM[N] and below.
 *
 * Calculated from bounding box area in degrees².
 */
export const HIDDEN_AT_ZOOM: Record<number, Set<string>> = {
  // Zoom 5: Hide countries with area < 0.5 deg² (very small islands, city-states)
  5: new Set([
    // Countries that overlap

    // North America
    'GT', // Guatemala
    'SV', // El Salvador
    'DO', // Dominican Republic

    // South America
    'SR', // Suriname

    // Europe
    'PT', // Portugal
    'IE', // Ireland
    'BE', // Belgium
    'NL', // Netherlands
    'BA', // Bosnia and Herzegovina
    'HU', // Hungary
    'RS', // Serbia
    'MK', // Macedonia
    'ME', // Montenegro
    'AL', // Albania
    'MD', // Moldova
    'CY', // Cyprus
    'PS', // Palestine
    'SI', // Slovenia
    'PO', // Poland
    'CH', // Switzerland
    'SK', // Slovakia
    'LT', // Lithuania

    // Africa
    'GM', // Gambia
    'SL', // Sierra Leone
    'CI', // Cote D'Ivoire
    'TG', // Togo
    'ST', // Sao Tome and Principe
    'CM', // Cameroon
    'BI', // Burundi

    // Asia
    'AZ', // Azerbaijan
    'JO', // Jordan
    'LB', // Lebanon
    'QA', // Qatar
    'BN', // Brunei

    // Micro-states and tiny islands (area < 0.1)
    'VA', // Vatican
    'AU', // Ashmore and Cartier Is. (not Australia - different code)
    'TV', // Tuvalu
    'PN', // Pitcairn Is.
    'NR', // Nauru
    'MC', // Monaco
    'MO', // Macao
    'BL', // St-Barthélemy
    'CK', // Cook Is.
    'NF', // Norfolk Island
    'MF', // St-Martin
    'SX', // Sint Maarten
    'GG', // Guernsey
    'MS', // Montserrat
    'SM', // San Marino
    'AS', // American Samoa
    'AI', // Anguilla
    'GD', // Grenada
    'NU', // Niue
    'JE', // Jersey
    'BM', // Bermuda
    'LI', // Liechtenstein
    'AW', // Aruba
    'IO', // Br. Indian Ocean Ter.
    'SC', // Seychelles
    'BB', // Barbados
    'BH', // Bahrain
    'AD', // Andorra
    'SG', // Singapore
    'LC', // Saint Lucia
    'DM', // Dominica
    'KN', // St. Kitts and Nevis
    'PM', // St. Pierre and Miquelon
    'MT', // Malta
    'GU', // Guam
    'AG', // Antigua and Barb.
    'HM', // Heard I. and McDonald Is.
    'TC', // Turks and Caicos Is.
    'IM', // Isle of Man
    'CW', // Curaçao
    'MV', // Maldives
    'VC', // St. Vin. and Gren.
    'VG', // British Virgin Is.
    'HK', // Hong Kong
    'MU', // Mauritius
    'AX', // Åland
    'VI', // U.S. Virgin Is.
    // Small countries (area 0.1 - 0.5)
    'LU', // Luxembourg
    'X238', // Siachen Glacier
  ]),

  // Zoom 6: Show more countries, still hide the tiniest
  6: new Set([
    // Only hide micro-states (area < 0.1)
    'VA', // Vatican
    'AU', // Ashmore and Cartier Is.
    'TV', // Tuvalu
    'PN', // Pitcairn Is.
    'NR', // Nauru
    'MC', // Monaco
    'MO', // Macao
    'BL', // St-Barthélemy
    'CK', // Cook Is.
    'NF', // Norfolk Island
    'MF', // St-Martin
    'SX', // Sint Maarten
    'GG', // Guernsey
    'MS', // Montserrat
    'SM', // San Marino
    'AS', // American Samoa
    'AI', // Anguilla
    'GD', // Grenada
    'NU', // Niue
    'JE', // Jersey
    'BM', // Bermuda
    'LI', // Liechtenstein
    'AW', // Aruba
    'IO', // Br. Indian Ocean Ter.
    'SC', // Seychelles
    'BB', // Barbados
    'BH', // Bahrain
    'AD', // Andorra
    'SG', // Singapore
    'LC', // Saint Lucia
    'DM', // Dominica
    'KN', // St. Kitts and Nevis
    'PM', // St. Pierre and Miquelon
    'MT', // Malta
    'GU', // Guam
    'AG', // Antigua and Barb.
    'HM', // Heard I. and McDonald Is.
    'TC', // Turks and Caicos Is.
    'IM', // Isle of Man
    'CW', // Curaçao
  ]),

  // Zoom 7+: Show almost everything, hide only the tiniest
  7: new Set([
    'VA', // Vatican
    'AU', // Ashmore and Cartier Is.
    'TV', // Tuvalu
    'PN', // Pitcairn Is.
    'NR', // Nauru
    'MC', // Monaco
    'MO', // Macao
    'BL', // St-Barthélemy
    'CK', // Cook Is.
    'NF', // Norfolk Island
    'MF', // St-Martin
    'SX', // Sint Maarten
    'GG', // Guernsey
    'MS', // Montserrat
    'SM', // San Marino
    'AS', // American Samoa
  ]),
}

/**
 * Check if a country label should be visible at a given zoom level.
 */
export function isCountryVisibleAtZoom(iso2: string, zoom: number): boolean {
  // Find the applicable zoom level (use highest available that's <= current zoom)
  const applicableZoom = Math.min(Math.max(5, Math.floor(zoom)), 7)
  const hiddenSet = HIDDEN_AT_ZOOM[applicableZoom]

  if (!hiddenSet) {
    return true // No restrictions at this zoom
  }

  return !hiddenSet.has(iso2)
}
